import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RAILSTREAM_LOGO } from "../lib/api";
import { Activity, ExternalLink, MapPin, Radio, RadioTower, Shield } from "lucide-react";
import { Link } from "react-router-dom";

const LoginPage = ({ onLogin, requires2FA, onVerify2FA, onCancel2FA }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    const result = await onLogin(username, password);
    if (result.error) {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    const result = await onVerify2FA(totpCode);
    if (result.error) {
      setError(result.error);
      setTotpCode("");
    }
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-cover bg-center px-4 py-8"
      style={{
        backgroundImage: "linear-gradient(90deg, rgba(5,5,7,0.9), rgba(5,5,7,0.7)), url('https://customer-assets.emergentagent.com/job_codec-server-test/artifacts/36pa2jrt_009%20EL%20E%20Bd..%20W%20of%20Corning%20NY%208-73%20%28Sized%29.jpg')"
      }}
    >
      <main className="grid w-full max-w-5xl overflow-hidden rounded-2xl border border-zinc-700 bg-zinc-950/95 shadow-2xl backdrop-blur-md lg:grid-cols-[1.15fr_0.85fr]">
        <section className="flex flex-col justify-between border-b border-zinc-800 p-6 sm:p-9 lg:border-b-0 lg:border-r">
          <div>
            <div className="mb-8 flex items-center gap-4">
              <img src={RAILSTREAM_LOGO} alt="RailStream" className="h-14 w-14 object-contain" />
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-amber-400">RailStream™</p>
                <p className="text-sm text-zinc-300">The RailStream operating platform</p>
              </div>
            </div>

            <div className="mb-4 inline-flex flex-wrap items-center gap-2 rounded-full border border-zinc-700 bg-zinc-900/80 px-3 py-1.5 text-sm font-medium text-zinc-200">
              <span>Camera</span><span className="text-amber-400">→</span>
              <span>RailNode™</span><span className="text-amber-400">→</span>
              <span>Processing</span><span className="text-amber-400">→</span>
              <span>Viewer</span>
            </div>
            <h1 className="max-w-xl text-3xl font-bold leading-tight text-white sm:text-4xl">
              Receiver to viewer.<br />Built and operated by RailStream.
            </h1>
            <p className="mt-4 max-w-xl text-base leading-7 text-zinc-200">
              RailStream™ designs, operates, monitors, processes, and distributes high-quality live railcams and railroad radio. Studio™ brings the field, processing, delivery, software, and operating record together.
            </p>

            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {[
                { icon: MapPin, label: "Location and equipment" },
                { icon: Activity, label: "RailNode™ monitoring" },
                { icon: RadioTower, label: "Processing and delivery" },
                { icon: Radio, label: "RailCast™ railroad radio" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2.5 rounded-lg border border-zinc-700 bg-zinc-900/80 px-3 py-3 text-sm font-medium text-zinc-100">
                  <Icon className="h-4 w-4 shrink-0 text-amber-400" />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 rounded-xl border border-amber-500/35 bg-amber-500/10 p-4">
            <p className="font-semibold text-white">Interested in hosting a railcam?</p>
            <p className="mt-1 text-sm leading-6 text-zinc-200">
              RailStream works with property owners, organizations, and railfans to bring exceptional trackside views online.
            </p>
            <a
              href="https://railstream.net/our-hosts"
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-400 hover:text-amber-300"
            >
              Learn about RailStream hosts <ExternalLink className="h-4 w-4" />
            </a>
            <span className="mx-3 text-zinc-600">|</span>
            <a
              href="https://app.railstream.net/"
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-200 hover:text-white"
            >
              Visit RailStream <ExternalLink className="h-4 w-4" />
            </a>
            <span className="mx-3 text-zinc-600">|</span>
            <Link to="/platform" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-300 hover:text-cyan-200">
              Platform and what&apos;s next
            </Link>
          </div>

          <a href="https://casestudies.ui.com/case-study/railstream" target="_blank" rel="noreferrer" className="mt-4 block rounded-xl border border-zinc-700 bg-zinc-900/80 p-4 transition-colors hover:border-zinc-500">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">Featured UniFi case study</p>
            <p className="mt-1 text-sm leading-6 text-zinc-200">How RailStream moves 15–20 TB/day through a resilient, self-hosted media operation.</p>
          </a>
        </section>

        <section className="p-6 sm:p-9">
          <div className="mb-7">
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-amber-400">Operator access</p>
            <h2 className="mt-2 text-2xl font-bold text-white">Sign in to RailStream Studio™</h2>
            <p className="mt-2 text-sm leading-6 text-zinc-300">Manage the RailStream network and its connected locations.</p>
          </div>

          {!requires2FA ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Input
                  data-testid="username-input"
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-12 border-zinc-600 bg-zinc-800 pl-10 text-white placeholder:text-zinc-300"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-300">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </span>
              </div>

              <div className="relative">
                <Input
                  data-testid="password-input"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 border-zinc-600 bg-zinc-800 pl-10 text-white placeholder:text-zinc-300"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-300">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </span>
              </div>

              {error && (
                <p data-testid="login-error" className="text-center text-sm font-medium text-red-400">{error}</p>
              )}

              <Button
                data-testid="login-submit"
                type="submit"
                className="h-12 w-full bg-amber-500 font-semibold text-black hover:bg-amber-400"
                disabled={loading}
              >
                {loading ? "Signing In..." : "Sign In"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div className="mb-4 text-center">
                <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/20">
                  <Shield className="h-8 w-8 text-amber-500" />
                </div>
                <h3 className="font-medium text-white">Two-Factor Authentication</h3>
                <p className="text-sm text-zinc-300">Enter the code from your authenticator app</p>
              </div>

              <Input
                data-testid="totp-input"
                type="text"
                placeholder="000000"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="h-14 border-zinc-600 bg-zinc-800 text-center text-2xl tracking-widest text-white"
                maxLength={6}
                autoFocus
              />

              {error && <p className="text-center text-sm font-medium text-red-400">{error}</p>}

              <Button
                data-testid="verify-2fa-submit"
                type="submit"
                className="h-12 w-full bg-amber-500 font-semibold text-black hover:bg-amber-400"
                disabled={loading || totpCode.length !== 6}
              >
                {loading ? "Verifying..." : "Verify"}
              </Button>

              <Button type="button" variant="ghost" className="w-full text-zinc-300 hover:text-white" onClick={onCancel2FA}>
                Back to Login
              </Button>
            </form>
          )}

          <div className="mt-7 border-t border-zinc-700 pt-5">
            <p className="text-center text-xs leading-relaxed text-zinc-300">
              Authorized RailStream personnel only. Activity is monitored and logged.
            </p>
          </div>
        </section>
      </main>

      <p className="mt-6 max-w-md px-2 text-center text-xs text-zinc-300">
        &copy; {new Date().getFullYear()} RailStream, LLC. All rights reserved. &mdash; Orange Cat Studios
        <span className="mt-1 block text-zinc-400">RailStream™, RailStream Studio™, RailNode™, and RailCast™ are trademarks of RailStream, LLC.</span>
        <span className="mt-2 flex flex-wrap justify-center gap-3"><Link className="hover:text-white" to="/terms">Terms</Link><Link className="hover:text-white" to="/privacy">Privacy</Link><Link className="hover:text-white" to="/acceptable-use">Acceptable use</Link><Link className="hover:text-white" to="/feed-provider-agreement">Feed providers</Link></span>
      </p>
    </div>
  );
};

export default LoginPage;
